import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'

const USER_PROJECTS_CACHE = jotai.atom<hooks.Cache<api.ProjectList>>({})

type UseUserProjectsConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UseUserProjectsParams
  withToken?: boolean
}

type UseUserProjectsParams = {
  limit?: number
  sort?: api.UserProjectSort
  state?: api.UserProjectState
  page?: number
}

type UseUserProjects = {
  response: hooks.Response<api.ProjectList>
  query: () => Promise<void>
}

export function useUserProjects(
  userId: number,
  { fetchOnMount = true, params = {}, withToken = false }: UseUserProjectsConfig = {},
): UseUserProjects {
  const sharedQuery = hooks.useSharedQuery(
    async (userId: number, params: UseUserProjectsParams, withToken: boolean) =>
      base.list(userId, {
        sort: params.sort,
        state: params.state,
        limit: params.limit,
        withToken,
      }),
    {
      atom: USER_PROJECTS_CACHE,
      id,
    },
  )

  React.useEffect(() => {
    if (fetchOnMount === 'force' && getResponse().status !== hooks.QueryStatus.PENDING) {
      query()
    } else if (fetchOnMount && getResponse().status === hooks.QueryStatus.PRISTINE) {
      query()
    }
  }, [id(userId, params, withToken)])

  return {
    response: getResponse(),
    query,
  }

  function getResponse(): hooks.Response<api.ProjectList> {
    if (userId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([userId, params, withToken])
  }

  function id(userId: number, params: UseUserProjectsParams, withToken: boolean): string {
    return `${String(userId)}:${JSON.stringify([params.limit, params.page, withToken])}`
  }

  async function query(): Promise<void> {
    if (userId === undefined) {
      return
    }

    return sharedQuery.query(userId, params, withToken)
  }
}
